import * as icons from 'icons';
import { UpgradeModule } from 'pages/NewOverview/components/UpgradeModule';
import { UpgradeModules } from 'pages/NewOverview/components/UpgradeModuleModal/constants';
import { IconNames } from 'pages/NewOverview/types';
import { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import ReactTooltip from 'react-tooltip';
import { colors } from 'styles/colors';
import { InfoTooltip } from '../../../components/InfoTooltip';
import {
  BetaContent,
  BigNumber,
  BigNumberCardContainer,
  BigNumberHeader,
  BigNumberIcon,
  ComingSoonBadge,
  Description,
  DetailsButton,
  DisabledDetailsText,
  IndicatorContainer,
  SubDescription,
} from './styles';

interface BigNumberCardProps {
  id: string;
  loading: boolean;
  icon: IconNames;
  number: string;
  subnumber?: string;
  description: { text: string; tooltip: ReactNode };
  subDescription: { number: number; text: string };
  onClickDetails?: () => void;
  detailsDisabledReasonKey?: string;
  disabledTooltip: boolean;
  dataTestId?: string;
  indicatorColor?: string;
  needUpgrade?: UpgradeModules;
  comingSoon?: boolean;
  breakLine?: boolean;
  beta?: boolean;
}

export function BigNumberCard({
  id,
  description,
  icon,
  loading,
  number,
  subDescription,
  onClickDetails,
  detailsDisabledReasonKey,
  disabledTooltip,
  dataTestId,
  indicatorColor,
  needUpgrade,
  comingSoon,
  breakLine,
  subnumber,
  beta,
}: Readonly<BigNumberCardProps>): ReactElement {
  const { t } = useTranslation();
  const detailsDisabled = !!detailsDisabledReasonKey;
  const showDetailsButton = (!!onClickDetails) || detailsDisabled;
  const tooltipId = `big-number-details-${id}`;

  if (loading) {
    return (
      <SkeletonCard />
    );
  }

  return (
    <BigNumberCardContainer data-testId={dataTestId}>
      <div>
        <BigNumber comingSoon={comingSoon}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BigNumberIcon>{renderBigNumberIcon(icon)}</BigNumberIcon>
            {indicatorColor !== undefined && (
            <Indicator
              color={needUpgrade || comingSoon ? colors.icon['disabled-300'] : indicatorColor}
            />
            )}
            <BigNumberHeader disabledColor={!!needUpgrade || !!comingSoon}>
              {needUpgrade || comingSoon ? '-' : number}
              <span style={{ fontSize: '16px' }}>{subnumber}</span>
            </BigNumberHeader>
            {beta && <BetaContent>Beta</BetaContent>}
          </div>
          <div>
            {showDetailsButton && !needUpgrade && !comingSoon && (
              detailsDisabled ? (
                <>
                  <ReactTooltip
                    id={tooltipId}
                    place="top"
                    type="dark"
                    effect="solid"
                    backgroundColor={colors.bg['surface-700']}
                    className="tooltip-bignumber"
                  >
                    <span>{t(detailsDisabledReasonKey ?? '')}</span>
                  </ReactTooltip>
                  <DisabledDetailsText
                    data-tip
                    data-for={tooltipId}
                  >
                    {t('verDetalhes')}
                  </DisabledDetailsText>
                </>
              ) : (
                <DetailsButton
                  variant="primary-ghost"
                  onClick={onClickDetails}
                  style={{ margin: 0 }}
                >
                  {t('verDetalhes')}
                </DetailsButton>
              )
            )}
            <div style={{ opacity: 1 }}>
              {needUpgrade && (
              <UpgradeModule module={needUpgrade} onlyButton />
              )}
              {comingSoon && (
              <ComingSoonBadge>
                {t('emBreve')}
              </ComingSoonBadge>
              )}

            </div>
          </div>
        </BigNumber>
        <Description>
          <h5 style={{ opacity: comingSoon ? 0.6 : 1 }}>{description.text}</h5>
          <InfoTooltip
            id={id}
            disabled={disabledTooltip}
          >
            {description.tooltip}
          </InfoTooltip>
        </Description>
        <SubDescription comingSoon={comingSoon} breakLine={breakLine}>
          <span>{needUpgrade || comingSoon ? '-' : subDescription.number}</span>
          <span>{subDescription.text}</span>
        </SubDescription>
      </div>
    </BigNumberCardContainer>
  );
}

function SkeletonCard(): ReactElement {
  return (
    <BigNumberCardContainer loading>
      <div>
        <BigNumber style={{ height: '40px' }}>
          <Skeleton
            width="32px"
            height="32px"
            borderRadius="8px"
            inline
          />
          <Skeleton
            width="154px"
            height="40px"
            borderRadius="100px"
            inline
          />
        </BigNumber>
        <Description style={{ height: '22px' }}>
          <Skeleton width="162px" height="22px" borderRadius="100px" inline />
          <Skeleton width="24px" height="24px" borderRadius="8px" inline />
        </Description>
        <Skeleton width="194px" height="22px" borderRadius="100px" />
      </div>
      <Skeleton
        width="92px"
        height="22px"
        borderRadius="100px"
        inline
        style={{ marginTop: '8px' }}
      />
    </BigNumberCardContainer>
  );
}

function renderBigNumberIcon(icon: IconNames): ReactElement {
  const Icon = icons[icon];

  return <Icon color={colors.icon['primary-base']} size="20" />;
}

function Indicator({ color }: Readonly<{ color: string }>): ReactElement {
  return (
    <IndicatorContainer color={color} />
  );
}
